import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AccountsReceivable } from '../models/AccountsReceivable';
import { ServiceOrdersService } from './service-orders.service';
import {
  ServiceOrderWorkSectionItem,
  ServiceOrderWorkSectionService,
  ServiceOrderWorkSectionsService
} from './service-order-work-sections.service';

@Injectable({ providedIn: 'root' })
export class ServiceOrderReceivableSyncService {
  private static readonly AMOUNT_TOLERANCE = 0.01;
  private static readonly SERVICE_ORDER_DOCUMENT_TYPE = 'Ordem de Serviço';
  private static readonly RECEIVED_STATUS = 'Recebido';
  private static readonly PAID_PAYMENT_STATUS = 'Pago';

  constructor(
    private serviceOrdersService: ServiceOrdersService,
    private workSectionsService: ServiceOrderWorkSectionsService
  ) {}

  syncWorkSectionPaymentAfterReceivable(ar: AccountsReceivable): Observable<void> {
    if (!this.shouldSync(ar)) {
      return of(void 0);
    }

    return this.resolveServiceOrderId(ar.documentNumber).pipe(
      switchMap(serviceOrderId => {
        if (!serviceOrderId) {
          console.warn(
            '[ServiceOrderReceivableSync] OS não encontrada para documentNumber:',
            ar.documentNumber
          );
          return of(void 0);
        }
        return this.syncFaultItemsPayment(serviceOrderId, ar.amountToReceive ?? 0);
      }),
      catchError(err => {
        console.warn('[ServiceOrderReceivableSync] Erro na sincronização:', err);
        return of(void 0);
      })
    );
  }

  private shouldSync(ar: AccountsReceivable): boolean {
    const docType = (ar.documentType || '').trim();
    const status = (ar.status || '').trim();
    return (
      docType === ServiceOrderReceivableSyncService.SERVICE_ORDER_DOCUMENT_TYPE &&
      status === ServiceOrderReceivableSyncService.RECEIVED_STATUS
    );
  }

  private resolveServiceOrderId(documentNumber: string): Observable<number | null> {
    const trimmed = (documentNumber || '').trim();
    if (!trimmed) {
      return of(null);
    }

    return this.serviceOrdersService.getPaginated(1, 1, { orderNumber: trimmed }).pipe(
      switchMap(res => {
        const id = res.serviceOrders?.[0]?.id ?? null;
        if (id) {
          return of(id);
        }

        const match = /^OS-(\d+)$/i.exec(trimmed);
        if (!match) {
          return of(null);
        }

        const parsedId = Number(match[1]);
        if (!Number.isFinite(parsedId) || parsedId <= 0) {
          return of(null);
        }

        return this.serviceOrdersService.getById(parsedId).pipe(
          map(os => os?.id ?? null),
          catchError(() => of(null))
        );
      }),
      catchError(() => of(null))
    );
  }

  private syncFaultItemsPayment(serviceOrderId: number, expectedAmount: number): Observable<void> {
    return this.workSectionsService.getByServiceOrderId(serviceOrderId).pipe(
      switchMap(sections => {
        if (!sections?.length) {
          console.warn('[ServiceOrderReceivableSync] Nenhuma work section encontrada para a OS.', serviceOrderId);
          return of(void 0);
        }

        const sectionLoads = sections.map(section =>
          forkJoin({
            sectionId: of(section.id),
            items: this.workSectionsService.getItemsByWorkSectionId(section.id).pipe(
              catchError(() => of([] as ServiceOrderWorkSectionItem[]))
            ),
            services: this.workSectionsService.getServicesByWorkSectionId(section.id).pipe(
              catchError(() => of([] as ServiceOrderWorkSectionService[]))
            )
          })
        );

        return forkJoin(sectionLoads).pipe(
          switchMap(loadedSections => {
            const faultRows = loadedSections.flatMap(({ items, services }) => [
              ...(items || []).filter(item => item.hasFault),
              ...(services || []).filter(service => service.hasFault)
            ]);

            if (faultRows.length === 0) {
              console.warn('[ServiceOrderReceivableSync] Nenhum item com hasFault encontrado.');
              return of(void 0);
            }

            const total = faultRows.reduce(
              (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.unitValue) || 0),
              0
            );
            const roundedTotal = Math.round(total * 100) / 100;

            if (Math.abs(roundedTotal - expectedAmount) > ServiceOrderReceivableSyncService.AMOUNT_TOLERANCE) {
              console.warn(
                `[ServiceOrderReceivableSync] Total de avarias (R$ ${roundedTotal}) não confere com o título (R$ ${expectedAmount}). Sync abortada.`
              );
              return of(void 0);
            }

            const updates = loadedSections
              .filter(({ items, services }) =>
                (items || []).some(item => item.hasFault) ||
                (services || []).some(service => service.hasFault)
              )
              .map(({ sectionId }) =>
                this.workSectionsService.updateItemsPaymentStatus(
                  sectionId,
                  ServiceOrderReceivableSyncService.PAID_PAYMENT_STATUS,
                  {
                    filterItem: item => item.hasFault,
                    filterService: service => service.hasFault,
                    showSuccessMessage: false
                  }
                )
              );

            if (updates.length === 0) {
              return of(void 0);
            }

            return forkJoin(updates).pipe(map(() => void 0));
          })
        );
      }),
      catchError(err => {
        console.warn('[ServiceOrderReceivableSync] Erro ao sincronizar paymentStatus:', err);
        return of(void 0);
      })
    );
  }
}
